// Compliance resolution, ported verbatim from the original (rule-engine
// reference §6–§7). `resolveFindingSuggestion` layers status overrides on top of
// `classifyFinding`; `calculateTransactionCompliance` is the authoritative
// per-transaction level. Refactored from global lookups to explicit inputs.

import { classifyFinding } from './classify';
import type { TxContext, Confidence } from './classify';
import { normalizeStatusValue, isClearedStatus, firstNonBlank } from './normalize';

export interface TransactionLike extends TxContext {
  raw?: Record<string, unknown>;
  legacyStatus?: string;
  selfDeclaration?: string;
}

export interface FindingLevelLike {
  finalCompliance?: string;
  suggestedCompliance?: string;
}

export interface Suggestion {
  level: string;
  baseLevel: string;
  anchors: string[];
  confidence: Confidence;
  route: string;
  matchedRules: string[];
  rationale?: string;
  effectiveStatus?: string;
}

// Highest-risk ranking across finding lines (lowest number wins).
const RANK: Record<string, number> = {
  'Non-Compliance: Possible IE': 1,
  'Self-Declared: Non-Compliance: Possible IE': 1,
  'Non-Compliant: Policy & Procedures': 2,
  'Self-Declared: Non-Compliant: Policy & Procedures': 2,
  'Non-Compliance Housekeeping': 3,
  'Self-Declared: Non-Compliance Housekeeping': 3,
  'Pending Review': 4,
  'Not Reviewed': 4,
  'Not Applicable': 5,
  'Self-Declared: Not Applicable': 5,
  Compliant: 6,
};

// Legacy compliance-level renames, applied at the READ boundary (db.ts mappers)
// so a row written before the Jun 2026 relabel ("Non-Conformance: Possible IE" →
// "Non-Compliance: Possible IE", Lindiwe feedback item 4) still classifies and
// displays as the current term until the data migration
// (scripts/relabelNonCompliance.mjs) lands. Forward-only: the canonical enum
// (domain/enums) already carries the new spelling, so deploy/migrate order is safe.
const COMPLIANCE_RENAMES: Record<string, string> = {
  'Non-Conformance: Possible IE': 'Non-Compliance: Possible IE',
  'Self-Declared: Non-Conformance: Possible IE': 'Self-Declared: Non-Compliance: Possible IE',
};

/** Coerce a stored compliance level onto the current vocabulary (legacy → new). */
export function canonicaliseComplianceLevel(level: string | null | undefined): string | undefined {
  if (level == null) return undefined;
  return COMPLIANCE_RENAMES[level] ?? level;
}

export function coupleSelfDeclaredLevel(level: string): string {
  const base = String(level || '').replace(/^Self-Declared:\s*/i, '');
  if (base === 'Deleted' || base === 'To delete') return base;
  if (base === 'Compliant' || base === 'Pending Review' || base === 'Not Reviewed') {
    return 'Self-Declared: Non-Compliant: Policy & Procedures';
  }
  return `Self-Declared: ${base}`;
}

function rawOf(tx: TransactionLike): Record<string, unknown> {
  return tx.raw ?? {};
}

function readStatus(tx: TransactionLike): string {
  const raw = rawOf(tx);
  return normalizeStatusValue(
    firstNonBlank(
      raw['Gaps Status'] as string,
      raw['Gaps Status (Resolved/Partially Resolved/ Unresolved/ Self-declared'] as string,
      raw['Gaps Status (Resolved/Partially Resolved/ Unresolved/ Self-declared)'] as string,
      raw['Status'] as string,
      raw['Statu '] as string,
      tx.legacyStatus,
    ),
  );
}

export function resolveFindingSuggestion(text: string, tx: TransactionLike = {}): Suggestion {
  const raw = rawOf(tx);
  const status = readStatus(tx);
  const base = classifyFinding(text, tx);
  const selfDecl = firstNonBlank(
    raw['Self-declaration Listing'] as string,
    raw['Self Declaration Listing'] as string,
    tx.selfDeclaration,
  );

  if (status === 'Deleted') {
    return { ...base, level: 'Deleted', effectiveStatus: status, rationale: 'Transaction status is Deleted.' };
  }
  if (status === 'To delete') {
    return { ...base, level: 'To delete', effectiveStatus: status, rationale: 'Transaction status is To delete.' };
  }
  if (isClearedStatus(status)) {
    return {
      ...base,
      level: 'Compliant',
      effectiveStatus: status,
      rationale: `Status is ${status}, so the compliance level is treated as Compliant regardless of the finding wording.`,
    };
  }
  if (status === 'Self-Declared') {
    if (!String(text || '').trim() && !selfDecl) {
      return {
        ...base,
        level: 'Pending Review',
        baseLevel: 'Pending Review',
        effectiveStatus: status,
        rationale: 'Status is Self-Declared, but there is no finding text or self-declaration detail yet.',
      };
    }
    const coupledBase =
      base.baseLevel === 'Pending Review' ? 'Not Applicable' : base.baseLevel || base.level || 'Pending Review';
    return {
      ...base,
      level: `Self-Declared: ${coupledBase}`,
      effectiveStatus: status,
      rationale: 'Status is Self-Declared, so the result is coupled with the findings-based classification.',
    };
  }
  return { ...base, level: base.level || 'Pending Review', effectiveStatus: status || 'Unspecified' };
}

export function safeSuggestion(text: string, tx: TransactionLike = {}): Suggestion {
  const r = resolveFindingSuggestion(text, tx);
  if (r && r.level) return r;
  return {
    level: 'Pending Review',
    baseLevel: 'Pending Review',
    anchors: ['Eskom Policies & Procedures'],
    confidence: 'Low',
    route: 'generic',
    matchedRules: [],
  };
}

export function calculateTransactionCompliance(
  tx: TransactionLike,
  findings: FindingLevelLike[] = [],
): string {
  const raw = rawOf(tx);
  const status = readStatus(tx);

  if (status === 'Deleted') return 'Deleted';
  if (status === 'To delete') return 'To delete';

  // The STRICT-RULE override is the in-app reviewer's verdict: every live editor writes it
  // (the working paper and the CES capture dual-write it with the level; the transaction
  // editor writes it directly). The secondary 'Compliance Level' source is what the
  // import / Manage leave behind and keeps the ported sheet conventions below.
  const strict = firstNonBlank(raw['Compliance level based on Updated Findings (Strict Rule)'] as string);
  const override = firstNonBlank(strict, raw['Compliance Level'] as string);
  // An explicit lifecycle sentinel chosen as the verdict IS the effective level (Lindiwe,
  // 29 Sep 2026 — she flags transactions for removal from the working paper and the
  // transaction update). It lands in the dashboard's Anomalies bucket (16 Sep), never in
  // Compliant. The ported rule ignored a sentinel here, so the register kept showing the
  // findings-derived level while the working paper said "To delete".
  if (strict === 'Deleted' || strict === 'To delete') return strict;
  if (status === 'Self-Declared' && override && !['Deleted', 'To delete'].includes(override)) {
    // An explicit 'Compliant' verdict is terminal — there is no tier to retain, so it is
    // NOT coupled into the Policy & Procedures tier. That remap (`coupleSelfDeclaredLevel`)
    // is the legacy sheet convention ('Self-declared' + 'Compliant' = declared P&P) and
    // still applies to an imported row's 'Compliance Level'; on a live edit it made
    // Compliant unreachable once the derived gaps status read self-declared (Lindiwe,
    // 29 Sep 2026: "we tried to update 4600080858 to compliant … it still remains as self
    // declared"). A TIER override keeps the R7 coupling: the transaction retains its tier.
    return strict === 'Compliant' ? 'Compliant' : coupleSelfDeclaredLevel(override);
  }
  if (override && !['Deleted', 'To delete'].includes(override)) return override;

  const lineLevels = findings
    .map((f) => f.finalCompliance || f.suggestedCompliance)
    .filter((v): v is string => Boolean(v));
  if (lineLevels.length) {
    const selected = [...lineLevels].sort((a, b) => (RANK[a] ?? 99) - (RANK[b] ?? 99))[0];
    return status === 'Self-Declared' ? coupleSelfDeclaredLevel(selected) : selected;
  }

  const findingsText =
    firstNonBlank(
      raw['Updated findings'] as string,
      raw['Updated Findings'] as string,
      raw['Findings'] as string,
    ) || firstNonBlank(raw['Initial Findings'] as string, raw['Initial findings'] as string);

  if (!findingsText) {
    const reviewed = String(
      firstNonBlank(
        raw['Reviewed Y/N'] as string,
        raw['Reviewed'] as string,
        raw['Reviewed YN'] as string,
        raw['Reviewed Yes/No'] as string,
      ),
    )
      .trim()
      .toLowerCase();
    if (reviewed === 'no' || reviewed === 'n') return 'Not Reviewed';
    if (isClearedStatus(status)) return 'Compliant';
    if (reviewed === 'yes' || reviewed === 'y') return 'Compliant';
    if (!status) return 'Pending Review';
    if (status === 'Self-Declared') return 'Self-Declared: Non-Compliant: Policy & Procedures';
    return 'Pending Review';
  }

  const auto = safeSuggestion(findingsText, tx);
  return status === 'Self-Declared' ? coupleSelfDeclaredLevel(auto.level) : auto.level;
}
